import * as React from 'react';
import { useEffect, useRef } from 'react';
import environmentService from '../../services/environment.service';

// Cloudflare's public always-passes test key, so localhost dev exercises the widget without a real site key.
const TEST_SITE_KEY = '1x00000000000000000000AA';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, params: {
        sitekey: string;
        callback: (token: string) => void;
        'expired-callback': () => void;
        'error-callback': () => void;
      }) => string;
      remove: (widgetId: string) => void;
    };
  }
}

// Undefined in production until VITE_TURNSTILE_SITE_KEY is set at build time — the widget then renders nothing and the server skips verification until its secret is configured.
export function turnstileSiteKey(): string | undefined {
  const configured = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  if (configured) return configured;
  return environmentService.isProduction() ? undefined : TEST_SITE_KEY;
}

let scriptPromise: Promise<void> | undefined;
function loadScript() {
  if (!scriptPromise) {
    scriptPromise = new Promise(resolve => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

interface Props {
  onToken: (token?: string) => void;
}

const Turnstile = ({ onToken }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    const siteKey = turnstileSiteKey();
    if (!siteKey) return;
    let widgetId: string | undefined;
    let removed = false;
    loadScript().then(() => {
      if (removed || !containerRef.current) return;
      widgetId = window.turnstile!.render(containerRef.current, {
        sitekey: siteKey,
        callback: token => onTokenRef.current(token),
        'expired-callback': () => onTokenRef.current(undefined),
        'error-callback': () => onTokenRef.current(undefined)
      });
    });
    return () => {
      removed = true;
      if (widgetId) window.turnstile!.remove(widgetId);
    };
  }, []);

  return <div ref={containerRef} />;
};

export default Turnstile;
